import React, { useState, useEffect } from 'react';
import './../../pagescss/PDFViewer.css';
import { useParams } from 'react-router-dom';
import TopNavbar from '../home/TopNavbar';

const PDF_CACHE_NAME = 'pdf-files-v1';

const PDFViewer = () => {
    const { pdfId, pdfName, fileId } = useParams();

    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('idle');

    const cacheKey = `/pdf/${fileId}`;

    // Check if PDF already exists in Cache Storage
    useEffect(() => {
        const checkPDF = async () => {
            try {
                const cache = await caches.open(PDF_CACHE_NAME);
                const cachedPDF = await cache.match(cacheKey);

                if (cachedPDF) {
                    setProgress(100);
                    setStatus('complete');
                }
            } catch (error) {
                console.error('Cache check error:', error);
            }
        };

        if (fileId) {
            checkPDF();
        }
    }, [fileId]);

    // Download PDF
    const downloadPDF = async () => {
        setIsLoading(true);
        setProgress(0);
        setStatus('downloading');

        try {
            const cache = await caches.open(PDF_CACHE_NAME);

            // Check cache first
            const cachedPDF = await cache.match(cacheKey);

            if (cachedPDF) {
                setProgress(100);
                setStatus('complete');
                setIsLoading(false);
                return;
            }

            const response = await fetch(`/pdfs/${pdfId}`);

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const total = parseInt(
                response.headers.get('Content-Length') || '0',
                10
            );

            let loaded = 0;
            const reader = response.body.getReader();
            const chunks = [];

            while (true) {
                const { done, value } = await reader.read();

                if (done) break;

                chunks.push(value);
                loaded += value.length;

                if (total > 0) {
                    setProgress(
                        Math.round((loaded / total) * 100)
                    );
                }
            }

            const blob = new Blob(chunks, {
                type: 'application/pdf'
            });

            const pdfResponse = new Response(blob, {
                headers: {
                    'Content-Type': 'application/pdf'
                }
            });

            // Save PDF in Cache Storage
            await cache.put(cacheKey, pdfResponse);

            console.log('PDF downloaded:', cacheKey);

            setProgress(100);
            setStatus('complete');
        } catch (error) {
            console.error('PDF download error:', error);
            setStatus('error');
            setProgress(0);
        } finally {
            setIsLoading(false);
        }
    };

    // Open PDF from Cache Storage
    const openPDF = async () => {
        try {
            const cache = await caches.open(PDF_CACHE_NAME);
            const cachedPDF = await cache.match(cacheKey);

            if (!cachedPDF) {
                console.error('PDF not found:', cacheKey);
                return;
            }

            const blob = await cachedPDF.blob();
            const url = URL.createObjectURL(blob);

            window.open(url);

            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 60000);
        } catch (error) {
            console.error('Error opening PDF:', error);
        }
    };

    return (
        <div className="container">
            <div className="header">
                <TopNavbar name="Download and view PDF files" />
            </div>

            <div className="pdf-card">
                <div className="pdf-info">
                    <div className="pdf-detail">
                        <h3>📄 {pdfName}</h3>
                    </div>
                </div>

                <div className="pdf-actions">
                    {status === 'complete' ? (
                        <>
                            <p
                                style={{
                                    fontSize: '10px',
                                    color: 'green'
                                }}
                            >
                                Downloaded
                            </p>

                            <button
                                className="btn btn-open"
                                onClick={openPDF}
                            >
                                📄 Open File
                            </button>
                        </>
                    ) : (
                        <button
                            className={`btn btn-download-view ${
                                isLoading ? 'loading' : ''
                            }`}
                            onClick={downloadPDF}
                            disabled={isLoading}
                        >
                            📄 {isLoading
                                ? 'Downloading...'
                                : 'Download File'}
                        </button>
                    )}

                    {(status === 'downloading' ||
                        status === 'complete') && (
                        <div className="progress-container">
                            <div
                                className="progress-bar"
                                style={{
                                    width: `${progress}%`
                                }}
                            >
                                <div className="progress-info">
                                    <span className="progress-percentage">
                                        {progress}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <p className="status-message status-error">
                            ❌ Download failed. Please try again.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PDFViewer;